import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useState,
  useEffect,
  type ReactNode,
} from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  Permission,
  PermissionOverride,
  StudioRole,
  StudioMembership,
  UserSession,
} from '@/types/permissions'
import { DEFAULT_PERMISSION_MATRIX } from '@/lib/auth/default-permissions'
import { logger } from '@/lib/logger'
import { rbacKeys } from '@/lib/hooks/use-rbac-me'

/**
 * Studio context value
 */
export interface StudioContextValue {
  // Current studio context
  currentStudioId: string | null
  currentStudio: StudioMembership | null
  studioRole: StudioRole | null
  permissions: Permission[]

  // All studio memberships for multi-studio users
  allStudios: StudioMembership[]

  // Studio switching
  setCurrentStudio: (studioId: string) => void

  // Permission overrides (for owners to customize)
  permissionOverrides: PermissionOverride[]
  isLoadingOverrides: boolean
  updatePermissionOverride: (
    role: Exclude<StudioRole, 'owner'>,
    permission: Permission,
    enabled: boolean
  ) => Promise<void>
  isUpdatingOverride: boolean

  // Computed permission matrix for UI
  getEffectivePermissions: (role: StudioRole) => Permission[]
  hasRolePermission: (role: StudioRole, permission: Permission) => boolean
}

const StudioContext = createContext<StudioContextValue | undefined>(undefined)

/**
 * Hook to access studio context
 */
export function useStudioContext(): StudioContextValue {
  const context = useContext(StudioContext)
  if (context === undefined) {
    throw new Error('useStudioContext must be used within a StudioProvider')
  }
  return context
}

/**
 * Props for StudioProvider
 */
interface StudioProviderProps {
  children: ReactNode
  session: UserSession | null
  getAccessToken: () => Promise<string | undefined>
}

/**
 * Studio context provider
 * Manages current studio, permissions, and permission overrides
 */
export function StudioProvider({
  children,
  session,
  getAccessToken,
}: StudioProviderProps) {
  const queryClient = useQueryClient()

  // Current studio ID (can be changed by user for multi-studio)
  const [currentStudioId, setCurrentStudioId] = useState<string | null>(
    session?.currentStudio?.studioId ?? null
  )

  // Update current studio when session changes
  useEffect(() => {
    if (session?.currentStudio?.studioId && !currentStudioId) {
      setCurrentStudioId(session.currentStudio.studioId)
    }
  }, [session, currentStudioId])

  // Get current studio membership
  const currentStudio = useMemo(() => {
    if (!currentStudioId || !session) return null
    return (
      session.allStudioMemberships.find((s) => s.studioId === currentStudioId) ??
      session.currentStudio
    )
  }, [currentStudioId, session])

  // Get current studio role and permissions
  const { studioRole, permissions } = useMemo(() => {
    if (!currentStudio) {
      return { studioRole: null, permissions: [] as Permission[] }
    }

    return {
      studioRole: currentStudio.role,
      permissions: currentStudio.permissions,
    }
  }, [currentStudio])

  // Fetch permission overrides for current studio (if owner)
  const {
    data: permissionOverrides = [],
    isLoading: isLoadingOverrides,
  } = useQuery({
    queryKey: ['permission-overrides', currentStudioId],
    queryFn: async (): Promise<PermissionOverride[]> => {
      if (!currentStudioId || studioRole !== 'owner') return []

      const token = await getAccessToken()
      if (!token) throw new Error('No access token')

      // TODO: Replace with real API call when backend is ready
      // const response = await fetch(
      //   `${API_BASE_URL}/studios/${currentStudioId}/permission-overrides`,
      //   { headers: { Authorization: `Bearer ${token}` } }
      // )
      // return response.json()

      // Mock response - no overrides by default
      return []
    },
    enabled: !!currentStudioId && studioRole === 'owner',
    staleTime: 5 * 60 * 1000,
  })

  // Mutation to update permission overrides
  const updateOverrideMutation = useMutation({
    mutationFn: async ({
      role,
      permission,
      enabled,
    }: {
      role: Exclude<StudioRole, 'owner'>
      permission: Permission
      enabled: boolean
    }) => {
      const token = await getAccessToken()
      if (!token) throw new Error('No access token')

      // TODO: Replace with real API call when backend is ready
      // await fetch(
      //   `${API_BASE_URL}/studios/${currentStudioId}/permission-overrides`,
      //   {
      //     method: 'PUT',
      //     headers: {
      //       Authorization: `Bearer ${token}`,
      //       'Content-Type': 'application/json',
      //     },
      //     body: JSON.stringify({ role, permission, enabled }),
      //   }
      // )

      logger.info('Permission override updated', {
        studioId: currentStudioId,
        role,
        permission,
        enabled,
      })

      // Return mock updated override
      return {
        studioId: currentStudioId!,
        role,
        permission,
        enabled,
        overriddenAt: new Date().toISOString(),
        overriddenBy: session?.user.id ?? 'unknown',
      } as PermissionOverride
    },
    onSuccess: (newOverride) => {
      // Update cache optimistically
      queryClient.setQueryData<PermissionOverride[]>(
        ['permission-overrides', currentStudioId],
        (old = []) => {
          const existing = old.findIndex(
            (o) =>
              o.role === newOverride.role &&
              o.permission === newOverride.permission
          )
          if (existing >= 0) {
            const updated = [...old]
            updated[existing] = newOverride
            return updated
          }
          return [...old, newOverride]
        }
      )

      // Invalidate RBAC cache to refresh permissions
      // This ensures any permission changes are immediately reflected
      queryClient.invalidateQueries({ queryKey: rbacKeys.me(session?.user.id) })
    },
    onError: (error) => {
      logger.error('Failed to update permission override', error)
    },
  })

  // Compute effective permissions for any role (base + overrides)
  const getEffectivePermissions = useCallback(
    (role: StudioRole): Permission[] => {
      // Owners always get all permissions
      if (role === 'owner') {
        return DEFAULT_PERMISSION_MATRIX.owner
      }

      const basePermissions = new Set(DEFAULT_PERMISSION_MATRIX[role])
      const roleOverrides = permissionOverrides.filter((o) => o.role === role)

      // Apply overrides
      for (const override of roleOverrides) {
        if (override.enabled) {
          basePermissions.add(override.permission)
        } else {
          basePermissions.delete(override.permission)
        }
      }

      return Array.from(basePermissions)
    },
    [permissionOverrides]
  )

  // Check if a role has a specific permission
  const hasRolePermission = useCallback(
    (role: StudioRole, permission: Permission): boolean => {
      return getEffectivePermissions(role).includes(permission)
    },
    [getEffectivePermissions]
  )

  // Switch current studio
  const handleSetCurrentStudio = useCallback(
    (studioId: string) => {
      setCurrentStudioId(studioId)
      // Invalidate RBAC cache when switching studios
      // This ensures fresh permissions are fetched for the new studio context
      queryClient.invalidateQueries({
        queryKey: rbacKeys.meWithStudio(session?.user.id, studioId),
      })
    },
    [queryClient, session?.user.id]
  )

  // Update permission override
  const handleUpdatePermissionOverride = useCallback(
    async (
      role: Exclude<StudioRole, 'owner'>,
      permission: Permission,
      enabled: boolean
    ) => {
      await updateOverrideMutation.mutateAsync({ role, permission, enabled })
    },
    [updateOverrideMutation]
  )

  const value = useMemo(
    () => ({
      currentStudioId,
      currentStudio,
      studioRole,
      permissions,
      allStudios: session?.allStudioMemberships ?? [],
      setCurrentStudio: handleSetCurrentStudio,
      permissionOverrides,
      isLoadingOverrides,
      updatePermissionOverride: handleUpdatePermissionOverride,
      isUpdatingOverride: updateOverrideMutation.isPending,
      getEffectivePermissions,
      hasRolePermission,
    }),
    [
      currentStudioId,
      currentStudio,
      studioRole,
      permissions,
      session,
      handleSetCurrentStudio,
      permissionOverrides,
      isLoadingOverrides,
      handleUpdatePermissionOverride,
      updateOverrideMutation.isPending,
      getEffectivePermissions,
      hasRolePermission,
    ]
  )

  return (
    <StudioContext.Provider value={value}>{children}</StudioContext.Provider>
  )
}
